/**
 * API statistiques Ministère de la Santé.
 * Base URL : /api/ministere/statistiques
 * Auth : Bearer JWT ministère obligatoire.
 */

import { apiFetch } from "./client";

const BASE = "/ministere/statistiques";

export type Period = "30j" | "60j" | "90j" | "6m" | "9m" | "12m" | "1a";

// ─── Types réponses ────────────────────────────────────────────────────────────

export interface SurveillanceGlobaleData {
  period: string;
  alertes: {
    actives: number;
    resolues: number;
    ignorees: number;
  };
  parNiveau: Record<string, number>;
  tauxResolution: number;
  maladiesFrequentes: Array<{
    maladie: string;
    nombreCas: number;
    evolution: number;
  }>;
  evolutionMensuelle: Array<{ periode: string; totalAlertes: number }>;
}

export interface EtatParMaladieItem {
  nom: string;
  categorie: string;
  type_seuil: string;
  action_immediate: boolean;
  statutAlerte: string;
  nombreCas: number;
  seuilAlerteFranchi: boolean;
  seuilEpidemiqueFranchi: boolean;
  evolution: string | null;
  gravite: string | null;
  regions: string[];
  derniereAlerte: string | null;
}

export interface CartographieData {
  alertesParDepartement: Record<string, number>;
  heatmapPopulation: Array<{
    region: string;
    nombre: number;
    pourcentage: number;
  }>;
  zonesRisque: Array<{
    zone: string;
    departements: string[];
    maladiesPrioritaires: string[];
    alertesActives: number;
    maladiesActives: string[];
  }>;
}

export interface SantePubliqueData {
  period: string;
  plateforme: {
    totalInscrits: number;
    nouveauxInscrits: number;
    professionnelsActifs: number;
    etablissementsActifs: number;
    consultationsRealisees: number;
    ordonnancesDelivrees: number;
    analysesPrescrites: number;
    tauxDeces?: number;
  };
  population: {
    repartitionAges: Array<{
      trancheAge: string;
      nombre: number;
      pourcentage: number;
    }>;
    repartitionGenres: Array<{
      genre: string;
      nombre: number;
      pourcentage: number;
    }>;
    repartitionRegions: Array<{
      region: string;
      nombre: number;
      pourcentage: number;
    }>;
    couvertureAssurance: {
      AMO: number;
      prive: number;
      mutuelle: number;
      autre: number;
      nonCouvert: number;
    };
  };
}

// ─── Fonctions fetch ───────────────────────────────────────────────────────────

export const fetchSurveillanceGlobale = (period: Period) =>
  apiFetch<SurveillanceGlobaleData>(
    `${BASE}/surveillance-globale?period=${period}`
  );

export const fetchEtatParMaladie = () =>
  apiFetch<EtatParMaladieItem[]>(`${BASE}/etat-par-maladie`);

export const fetchCartographie = () =>
  apiFetch<CartographieData>(`${BASE}/cartographie`);

export const fetchSantePublique = (period: Period) =>
  apiFetch<SantePubliqueData>(`${BASE}/sante-publique?period=${period}`);
